/**
 * Detects if the website is being viewed in a native app webview or browser
 * @returns {Object} Detection results
 */
export const detectAppVsBrowser = () => {
    const ua = navigator.userAgent;

    // Common app indicators in user agent
    const isIosApp =
        /^(?!.*\bCriOS\b).*AppleWebKit/i.test(ua) &&
        !/Safari/.test(ua) &&
        /iPad|iPhone|iPod/.test(ua);

    const isAndroidApp = /Linux; Android/.test(ua) && !/Chrome|Firefox|Opera|Edge/.test(ua);

    // Check for specific app frameworks
    const isCordovaApp = !!window.cordova;
    const isReactNativeApp = !!window.navigator.product === 'ReactNative';
    const isFlutterApp = !!window.flutter;
    const isTauriApp = !!window.__TAURI__;

    // Check for X-Requested-With header (Android WebView indicator)
    const isAndroidWebView = /wv|android/.test(ua) && !/Chrome/.test(ua);

    // Check if running in WeChat, Facebook, or other social app browsers
    const isWeChatApp = /MicroMessenger/.test(ua);
    const isFacebookApp = /FBAN|FBAV/.test(ua);
    const isInstagramApp = /Instagram/.test(ua);
    const isLinkedInApp = /LinkedInApp/.test(ua);
    const isTwitterApp = /Twitter/.test(ua);

    return {
        isNativeApp:
            isIosApp ||
            isAndroidApp ||
            isCordovaApp ||
            isReactNativeApp ||
            isFlutterApp ||
            isTauriApp,
        isWebView: isAndroidWebView || isIosApp || isAndroidApp,
        inAppBrowser:
            isWeChatApp || isFacebookApp || isInstagramApp || isLinkedInApp || isTwitterApp,
        specifics: {
            isIosApp,
            isAndroidApp,
            isCordovaApp,
            isReactNativeApp,
            isFlutterApp,
            isTauriApp,
            isWeChatApp,
            isFacebookApp,
            isInstagramApp,
            isLinkedInApp,
            isTwitterApp,
        },
    };
};

/**
 * Detects the browser name and version
 * @returns {Object} Browser information
 */
export const detectBrowser = () => {
    const ua = navigator.userAgent;

    let browser = 'Unknown';
    let version = 'Unknown';
    let engine = 'Unknown';

    // Detect rendering engine first
    if (/Gecko/.test(ua)) {
        engine = 'Gecko';
    } else if (/WebKit|AppleWebKit/.test(ua)) {
        engine = 'WebKit';
    } else if (/Trident/.test(ua)) {
        engine = 'Trident';
    }

    // Detect browser - order matters! (Chrome contains Safari string, etc.)
    if (/Firefox|FxiOS/.test(ua)) {
        browser = 'Firefox';
        version = ua.match(/Firefox[\/\s](\d+\.\d+)/)?.[1] || 'Unknown';
    } else if (/Edge|EdgA|EdgiOS/.test(ua)) {
        browser = 'Edge';
        version = ua.match(/Edge[\/\s](\d+\.\d+)/)?.[1] || 'Unknown';
    } else if (/Chrome|CriOS|Chromium/.test(ua) && !/Edge/.test(ua)) {
        browser = 'Chrome';
        version = ua.match(/Chrome[\/\s](\d+\.\d+)/)?.[1] || 'Unknown';
    } else if (/Safari/.test(ua) && !/Chrome|Edge/.test(ua)) {
        browser = 'Safari';
        // Safari shows version in different format
        version = ua.match(/Version[\/\s](\d+\.\d+)/)?.[1] || 'Unknown';
    } else if (/OPR|Opera/.test(ua)) {
        browser = 'Opera';
        version = ua.match(/OPR[\/\s](\d+\.\d+)|Opera[\/\s](\d+\.\d+)/)?.[1] || 'Unknown';
    } else if (/MSIE|Trident/.test(ua)) {
        browser = 'Internet Explorer';
        version = ua.match(/MSIE\s(\d+\.\d+)|rv:(\d+\.\d+)/)?.[1] || 'Unknown';
    }

    return {
        browser,
        version,
        engine,
        userAgent: ua,
    };
};

/**
 * Detects device and platform information
 * @returns {Object} Device information
 */
export const detectDevice = () => {
    const ua = navigator.userAgent;
    let device

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isTablet = /iPad|Android (?!.*Mobile)/.test(ua);
    const isDesktop = !isMobile && !isTablet;

    if (isMobile) device = 'mobile';
    if (isTablet) device = 'tablet';
    if (isDesktop) device = 'desktop';

    let os = 'Unknown';
    let osVersion = 'Unknown';

    if (/Windows NT/.test(ua)) {
        os = 'Windows';
        osVersion = ua.match(/Windows NT ([0-9.]+)/)?.[1] || 'Unknown';
    } else if (/Macintosh/.test(ua)) {
        os = 'macOS';
        osVersion = ua.match(/Mac OS X ([0-9_.]+)/)?.[1]?.replace(/_/g, '.') || 'Unknown';
    } else if (/Android/.test(ua)) {
        os = 'Android';
        osVersion = ua.match(/Android ([0-9.]+)/)?.[1] || 'Unknown';
    } else if (/iPhone|iPad|iPod/.test(ua)) {
        os = 'iOS';
        osVersion = ua.match(/OS ([0-9_]+)/)?.[1]?.replace(/_/g, '.') || 'Unknown';
    } else if (/Linux/.test(ua)) {
        os = 'Linux';
    }

    return {
        isMobile,
        isTablet,
        isDesktop,
        device,
        os,
        osVersion,
        touchEnabled: () => 'maxTouchPoints' in navigator && navigator.maxTouchPoints > 0,
    };
};

/**
 * Complete detection utility combining all checks
 * @returns {Object} Complete browser, device and app information
 */
export const getEnvironmentInfo = () => {
    const browser = detectBrowser();
    const device = detectDevice();
    const appInfo = detectAppVsBrowser();

    return {
        browser,
        device,
        app: appInfo,
        isBrowser: !appInfo.isNativeApp && !appInfo.inAppBrowser,
        isInApp: appInfo.isNativeApp || appInfo.inAppBrowser,
        isStandaloneApp: appInfo.isNativeApp,
        isEmbeddedBrowser: appInfo.inAppBrowser,
        timestamp: new Date().toISOString(),
    };
};
